import React, { useState, useRef, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { apiService } from '../../../../services/api';
import { CreateAnotacaoData } from '../../../../types/notes';

interface NotesModalProps {
    showNotesModal: boolean;
    setShowNotesModal: (show: boolean) => void;
    opportunityId: number;
    onNoteCreated?: () => void;
}

interface UploadedFile {
    file: File;
    size: string;
    name: string;
}

export const NotesModal: React.FC<NotesModalProps> = ({
    showNotesModal,
    setShowNotesModal,
    opportunityId,
    onNoteCreated
}) => {
    const [noteTitle, setNoteTitle] = useState('');
    const [noteContent, setNoteContent] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newFiles = Array.from(files).map(file => ({
                file: file,
                size: formatFileSize(file.size),
                name: file.name
            }));
            setUploadedFiles(prev => [...prev, ...newFiles]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const resetForm = () => {
        setNoteTitle('');
        setNoteContent('');
        setUploadedFiles([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClose = () => {
        resetForm();
        setShowNotesModal(false);
    };

    const handleSaveNote = async () => {
        if (!noteTitle.trim()) {
            alert('O título é obrigatório');
            return;
        }

        try {
            setIsSubmitting(true);
            const formData = new FormData();
            formData.append('id_oportunidade', opportunityId.toString());
            formData.append('titulo', noteTitle);
            formData.append('observacoes', noteContent);

            // Adiciona múltiplos arquivos ao FormData
            uploadedFiles.forEach(fileData => {
                formData.append('arquivos[]', fileData.file);
            });

            const response = await apiService.createAnotacao({
                id_oportunidade: opportunityId,
                titulo: noteTitle,
                observacoes: noteContent,
                arquivos: uploadedFiles.map(f => f.file)
            });

            if (response.success) {
                setShowSuccessModal(true);
                resetForm();
                onNoteCreated?.();
            }
        } catch (error) {
            console.error('Erro ao salvar anotação:', error);
            alert('Erro ao salvar a anotação. Por favor, tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Modal 
                show={showNotesModal} 
                onHide={handleClose}
                centered
                className="custom-modal modal-padding"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Adicionar Nova Anotação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={(e) => e.preventDefault()}>
                        <div className="mb-3">
                            <label className="col-form-label">
                                Título <span className="text-danger">*</span>
                            </label>
                            <input 
                                className="form-control" 
                                type="text"
                                value={noteTitle}
                                onChange={(e) => setNoteTitle(e.target.value)}
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label className="col-form-label">
                                Anotação <span className="text-danger">*</span>
                            </label>
                            <textarea 
                                className="form-control" 
                                rows={4}
                                value={noteContent}
                                onChange={(e) => setNoteContent(e.target.value)}
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label className="col-form-label">
                                Anexo
                            </label>
                            <div className="drag-attach">
                                <input 
                                    type="file" 
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    multiple
                                    accept="*/*"
                                />
                                <div className="img-upload">
                                    <i className="ti ti-file-broken" />
                                    Enviar Arquivo
                                </div>
                            </div>
                        </div>
                        <div className="mb-3">
                            <label className="col-form-label">Arquivos Enviados</label>
                            {uploadedFiles.map((fileData, index) => (
                                <div key={index} className="upload-file upload-list">
                                    <div>
                                        <h6>{fileData.name}</h6>
                                        <p>{fileData.size}</p>
                                    </div>
                                    <button 
                                        onClick={() => handleRemoveFile(index)}
                                        className="text-danger border-0 bg-transparent"
                                        type="button"
                                    >
                                        <i className="ti ti-trash-x" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="col-lg-12 text-end modal-btn">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSaveNote}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Salvando...
                                    </>
                                ) : (
                                    'Confirmar'
                                )}
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            {/* Modal de Sucesso */}
            <Modal 
                show={showSuccessModal} 
                onHide={() => {
                    setShowSuccessModal(false);
                    setShowNotesModal(false);
                }}
                centered
            >
                <div className="modal-body">
                    <div className="success-message text-center">
                        <div className="success-popup-icon bg-success-light">
                            <i className="ti ti-check-check fs-36 text-success" />
                        </div>
                        <h3>Anotação Criada com Sucesso!</h3>
                        <p>A anotação foi adicionada à oportunidade.</p>
                        <div className="col-lg-12 text-center modal-btn">
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    setShowNotesModal(false);
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};